/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { find, uniqBy, clone, map, each } from 'lodash';
import { Select } from 'semantic-ui-react';

import {
    addInvoiceProduct,
    deleteInvoiceProduct,
} from '../../../../../actions/invoice';
import { MASTER_ENTITY_FIELDS_FRAGMENT } from '../../../containers/masterAccessQueries';

// The query that gets products and services that the salon provide
const GET_ENTITIES_QUERY = gql`
    query getTitles(
        $salon: ID!
        $product: ID!
        $withProduct: Boolean!
        $lang: String!
        $service: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    products {
                        nodes {
                            id
                            ...product
                        }
                    }
                    product(id: $product) @include(if: $withProduct) {
                        id
                        ...product
                    }
                    serviceGroups(service: $service) {
                        nodes {
                            id
                            services {
                                id
                                ...service
                            }
                        }
                    }
                }
            }
            ...onMasterEntityFields
        }
    }

    fragment product on Product {
        name
        price
        quantity
    }

    fragment service on Service {
        name(lang: $lang)
        price
    }
    ${MASTER_ENTITY_FIELDS_FRAGMENT}
`;

const formatOption = (description, option) => ({
    text: option.name,
    value: option.id,
    description,
});

type EntityFieldProps = {
    value: ?string,
    onChange: Function,
    name: string,
    fieldPath: string,
    paid: boolean,
    change: Function,
};

function EntityField({
    value,
    onChange,
    name,
    fieldPath,
    paid,
    change,
    ...rest
}: EntityFieldProps) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const dispatch = useDispatch();

    // Read the `type` field of the same item from the form context
    const typeName = fieldPath ? `${fieldPath}.type` : 'type';
    const type = useWatch({ name: typeName });

    const { data, loading } = useQuery(GET_ENTITIES_QUERY, {
        variables: {
            salon,
            product: value || '',
            withProduct: !!value,
            lang: currentLanguage,
            service: value || null,
        },
        fetchPolicy: 'cache-and-network',
        skip: !salon,
    });

    let options = [];
    let entities = [];

    if (!loading && data && data.viewer) {
        const { viewer } = data;
        if (type === 'PRODUCT') {
            if (viewer.salon.product) {
                entities = clone(viewer.salon.products.nodes);
                entities.push(viewer.salon.product);
            } else {
                entities = viewer.salon.products.nodes;
            }
            entities = uniqBy(entities, 'id');
            options = map(entities, item => formatOption(item.quantity, item));
        } else if (type === 'SERVICE') {
            each(viewer.salon.serviceGroups.nodes, group =>
                each(group.services, service => {
                    options.push(formatOption('Service', service));
                    entities.push(service);
                }),
            );
        }
    }

    const handleChange = async (e, { value: v }) => {
        const entity = find(entities, { id: v });
        const namePrefix = name.replace('entity', '');
        if (entity) {
            change(namePrefix + 'price', parseFloat(entity.price).toFixed(2));
            if (!paid) {
                if (type === 'PRODUCT') {
                    await dispatch(addInvoiceProduct(name, entity));
                }
                if (type === 'SERVICE') {
                    await dispatch(deleteInvoiceProduct(name));
                }

                const getQuantity = () => {
                    if (type === 'SERVICE') return 1;
                    if (type === 'PRODUCT') return entity.quantity ? 1 : 0;
                    return 0;
                };

                change(namePrefix + 'quantity', getQuantity());
                change(namePrefix + 'paymentType', 'CASH');
            }
        }
        setTimeout(() => onChange(e, { value: v }), 0);
    };

    return (
        <Select
            value={value}
            onChange={handleChange}
            options={options}
            loading={loading}
            disabled={!type || rest.disabled}
            {...rest}
        />
    );
}

export default EntityField;
